import React, { useEffect, useState, useCallback } from 'react';
import {
  ScrollText, RefreshCw, Trash2, Search, Filter,
  CheckCircle, AlertCircle, Clock, Activity, Users, Zap
} from 'lucide-react';
import { getApiBase } from '../utils/apiBase';

const API_BASE = getApiBase();

interface Log {
  _id: string;
  method: string;
  url: string;
  status: number;
  responseTimeMs: number;
  ip: string;
  userAgent: string;
  userId: string | null;
  userEmail: string | null;
  userRole: string | null;
  summary: string | null;
  category: string | null;
  createdAt: string;
}

interface LogStats {
  totalRequests: number;
  errorCount: number;
  successCount: number;
  avgResponseMs: number;
  statusBreakdown: { status: number; count: number }[];
  topUrls: { url: string; count: number }[];
  topUsers: { email: string; count: number }[];
}

interface Meta {
  total: number;
  page: number;
  limit: number;
  pages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const METHOD_COLORS: Record<string, { bg: string; color: string }> = {
  GET:    { bg: '#dbeafe', color: '#1d4ed8' },
  POST:   { bg: '#d1fae5', color: '#065f46' },
  PUT:    { bg: '#fef3c7', color: '#92400e' },
  PATCH:  { bg: '#fef3c7', color: '#92400e' },
  DELETE: { bg: '#fee2e2', color: '#991b1b' },
};

// Category colour coding — payments get a gold accent so they stand out
const CATEGORY_COLORS: Record<string, { bg: string; color: string }> = {
  payments:     { bg: '#fef3c7', color: '#92400e' },
  auth:         { bg: '#ede9fe', color: '#5b21b6' },
  stores:       { bg: '#dbeafe', color: '#1d4ed8' },
  products:     { bg: '#d1fae5', color: '#065f46' },
  promotions:   { bg: '#fce7f3', color: '#9d174d' },
  transactions: { bg: '#f3f4f6', color: '#374151' },
  users:        { bg: '#e0f2fe', color: '#0369a1' },
  chat:         { bg: '#f0fdf4', color: '#166534' },
  logs:         { bg: '#f8f7f5', color: '#6b7280' },
  api:          { bg: '#f3f4f6', color: '#6b7280' },
};

// Derive category from URL when the DB field is null/missing (old log documents).
// Runs client-side so filtering works immediately without a backend reindex.
const deriveCategory = (url: string, stored: string | null): string => {
  if (stored && stored !== 'api') return stored;
  if (url.includes('/api/payments'))    return 'payments';
  if (url.includes('/api/auth'))        return 'auth';
  if (url.includes('/api/users'))       return 'users';
  if (url.includes('/api/stores'))      return 'stores';
  if (url.includes('/api/products'))    return 'products';
  if (url.includes('/api/promotions'))  return 'promotions';
  if (url.includes('/api/transactions'))return 'transactions';
  if (url.includes('/api/chat'))        return 'chat';
  if (url.includes('/api/reports'))     return 'reports';
  if (url.includes('/api/logs'))        return 'logs';
  if (url.includes('/api/uploads'))     return 'uploads';
  if (url.includes('/api/dashboard'))   return 'dashboard';
  if (url.includes('/api/analytics'))   return 'analytics';
  if (url.includes('/api/inventory'))   return 'inventory';
  return 'api';
};

const statusColor = (status: number) => {
  if (status < 300) return { bg: '#d1fae5', color: '#065f46' };
  if (status < 400) return { bg: '#dbeafe', color: '#1d4ed8' };
  if (status < 500) return { bg: '#fef3c7', color: '#92400e' };
  return { bg: '#fee2e2', color: '#991b1b' };
};

const durationColor = (ms: number) => {
  if (ms < 200)  return '#059669';
  if (ms < 1000) return '#d97706';
  return '#dc2626';
};

const Pill: React.FC<{ bg: string; color: string; children: React.ReactNode }> = ({ bg, color, children }) => (
  <span style={{
    background: bg, color, fontSize: '11px', fontWeight: 600,
    padding: '3px 8px', borderRadius: '20px', whiteSpace: 'nowrap',
  }}>
    {children}
  </span>
);

const StatCard: React.FC<{
  label: string; value: string | number;
  icon: React.ReactNode; accent: string; bg: string; sub?: string;
}> = ({ label, value, icon, accent, bg, sub }) => (
  <div style={{
    background: '#fff', borderRadius: '14px', padding: '20px 24px',
    border: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: '12px',
  }}>
    <div style={{
      background: bg, borderRadius: '10px', width: '38px', height: '38px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent,
    }}>
      {icon}
    </div>
    <div>
      <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>{label}</p>
      <p style={{ margin: '4px 0 0', fontSize: '26px', fontWeight: 600, color: '#111827', lineHeight: 1 }}>{value}</p>
      {sub && <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9ca3af' }}>{sub}</p>}
    </div>
  </div>
);

const CATEGORIES = ['all', 'payments', 'auth', 'stores', 'products', 'promotions', 'transactions', 'users', 'chat', 'logs'];

const Logs: React.FC = () => {
  const token = localStorage.getItem('authToken');

  const [logs, setLogs]           = useState<Log[]>([]);
  const [stats, setStats]         = useState<LogStats | null>(null);
  const [meta, setMeta]           = useState<Meta | null>(null);
  const [loading, setLoading]     = useState(false);
  const [clearing, setClearing]   = useState(false);
  const [selectedLog, setSelectedLog] = useState<Log | null>(null);

  // Filters
  const [page, setPage]               = useState(1);
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatus]     = useState('');
  const [methodFilter, setMethod]     = useState('');
  const [emailFilter, setEmail]       = useState('');
  const [minDuration, setMinDuration] = useState('');
  const [fromDate, setFromDate]       = useState('');
  const [toDate, setToDate]           = useState('');
  const [categoryFilter, setCategory] = useState('all');

  const fetchStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set('fromDate', fromDate);
      if (toDate)   params.set('toDate', toDate);
      const res = await fetch(`${API_BASE}/api/logs/stats?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setStats(data.data);
    } catch (err) {
      console.error('fetchStats error:', err);
    }
  }, [token, fromDate, toDate]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (search)                        params.set('url', search);
      if (statusFilter)                  params.set('status', statusFilter);
      if (methodFilter)                  params.set('method', methodFilter);
      if (emailFilter)                   params.set('userEmail', emailFilter);
      if (minDuration)                   params.set('minDuration', minDuration);
      if (fromDate)                      params.set('fromDate', fromDate);
      if (toDate)                        params.set('toDate', toDate);
      if (categoryFilter && categoryFilter !== 'all') params.set('category', categoryFilter);

      const res = await fetch(`${API_BASE}/api/logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setLogs(data.data);
        setMeta(data.meta);
      }
    } catch (err) {
      console.error('fetchLogs error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, page, search, statusFilter, methodFilter, emailFilter, minDuration, fromDate, toDate, categoryFilter]);

  useEffect(() => { fetchLogs(); fetchStats(); }, [fetchLogs, fetchStats]);

  const handleClear = async () => {
    if (!window.confirm('Delete ALL logs? This cannot be undone.')) return;
    setClearing(true);
    try {
      await fetch(`${API_BASE}/api/logs`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs([]);
      setMeta(null);
      setStats(null);
    } finally {
      setClearing(false);
    }
  };

  const resetFilters = () => {
    setSearch(''); setStatus(''); setMethod('');
    setEmail(''); setMinDuration(''); setFromDate(''); setToDate('');
    setCategory('all'); setPage(1);
  };

  const inputStyle: React.CSSProperties = {
    border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 12px',
    fontSize: '13px', color: '#374151', background: '#fff', outline: 'none',
  };

  const selectStyle: React.CSSProperties = { ...inputStyle, cursor: 'pointer' };

  return (
    <div style={{ padding: '32px', background: '#f8f7f5', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@500&display=swap');`}</style>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>System</p>
          <h1 style={{ margin: '4px 0 0', fontSize: '28px', fontWeight: 600, color: '#111827' }}>API Logs</h1>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => { fetchLogs(); fetchStats(); }}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', borderRadius: '10px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '13px', fontWeight: 500, color: '#374151', cursor: 'pointer' }}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={handleClear} disabled={clearing}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', borderRadius: '10px', border: 'none', background: '#fee2e2', fontSize: '13px', fontWeight: 500, color: '#dc2626', cursor: 'pointer' }}>
            <Trash2 size={14} /> {clearing ? 'Clearing...' : 'Clear All'}
          </button>
        </div>
      </div>

      {/* ── Stat Cards ─────────────────────────────────────────────────── */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '28px' }}>
          <StatCard label="Total Requests" value={stats.totalRequests.toLocaleString()} icon={<Activity size={18} />} accent="#F1561D" bg="#fff5f2" />
          <StatCard label="Errors (4xx/5xx)" value={stats.errorCount.toLocaleString()} icon={<AlertCircle size={18} />} accent="#dc2626" bg="#fee2e2"
            sub={`${stats.totalRequests ? Math.round((stats.errorCount / stats.totalRequests) * 100) : 0}% error rate`} />
          <StatCard label="Avg Response" value={`${stats.avgResponseMs}ms`} icon={<Clock size={18} />} accent="#0ea5e9" bg="#f0f9ff" />
          <StatCard label="Successful" value={stats.successCount.toLocaleString()} icon={<CheckCircle size={18} />} accent="#059669" bg="#f0fdf4" />
        </div>
      )}

      {/* ── Top URLs + Top Users ────────────────────────────────────────── */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '28px' }}>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '20px 24px', border: '1px solid #f3f4f6' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Zap size={14} color="#F1561D" /> Most Hit Endpoints
            </h3>
            {stats.topUrls.map((u, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < stats.topUrls.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#374151', fontFamily: "'DM Mono', monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%' }}>{u.url}</p>
                <Pill bg="#f3f4f6" color="#374151">{u.count}</Pill>
              </div>
            ))}
          </div>

          <div style={{ background: '#fff', borderRadius: '14px', padding: '20px 24px', border: '1px solid #f3f4f6' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={14} color="#6366f1" /> Most Active Users
            </h3>
            {stats.topUsers.length === 0
              ? <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>No authenticated requests yet</p>
              : stats.topUsers.map((u, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < stats.topUsers.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#374151' }}>{u.email}</p>
                  <Pill bg="#f3f4f6" color="#374151">{u.count} reqs</Pill>
                </div>
              ))
            }
          </div>
        </div>
      )}

      {/* ── Category Quick Filters ──────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {CATEGORIES.map(cat => {
          const c = cat === 'all' ? { bg: '#111827', color: '#fff' } : (CATEGORY_COLORS[cat] || { bg: '#f3f4f6', color: '#374151' });
          const isActive = categoryFilter === cat;
          return (
            <button
              key={cat}
              onClick={() => { setCategory(cat); setPage(1); }}
              style={{
                padding: '6px 14px', borderRadius: '20px', cursor: 'pointer',
                fontSize: '12px', fontWeight: 600, textTransform: 'capitalize',
                background: isActive ? c.bg : '#fff',
                color:      isActive ? c.color : '#9ca3af',
                border:     isActive ? 'none' : '1px solid #e5e7eb',
                transition: 'all 0.15s',
              } as React.CSSProperties}
            >
              {cat}
            </button>
          );
        })}
      </div>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <div style={{ background: '#fff', borderRadius: '14px', padding: '20px 24px', border: '1px solid #f3f4f6', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Filter size={14} color="#9ca3af" />
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Filters</span>
          <button onClick={resetFilters} style={{ marginLeft: 'auto', fontSize: '12px', color: '#F1561D', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>
            Reset all
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input placeholder="Search URL..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              style={{ ...inputStyle, paddingLeft: '32px', width: '100%', boxSizing: 'border-box' }} />
          </div>
          <input placeholder="User email..." value={emailFilter} onChange={e => { setEmail(e.target.value); setPage(1); }}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
          <select value={statusFilter} onChange={e => { setStatus(e.target.value); setPage(1); }} style={{ ...selectStyle, width: '100%', boxSizing: 'border-box' }}>
            <option value="">All statuses</option>
            <option value="200">200 OK</option>
            <option value="201">201 Created</option>
            <option value="400">400 Bad Request</option>
            <option value="401">401 Unauthorized</option>
            <option value="403">403 Forbidden</option>
            <option value="404">404 Not Found</option>
            <option value="500">500 Server Error</option>
          </select>
          <select value={methodFilter} onChange={e => { setMethod(e.target.value); setPage(1); }} style={{ ...selectStyle, width: '100%', boxSizing: 'border-box' }}>
            <option value="">All methods</option>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
            <option value="DELETE">DELETE</option>
          </select>
          <input placeholder="Min duration (ms)" type="number" value={minDuration} onChange={e => { setMinDuration(e.target.value); setPage(1); }}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
          <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(1); }}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
          <input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPage(1); }}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
        </div>
      </div>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #f3f4f6', overflow: 'hidden' }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>
            {meta ? `${meta.total.toLocaleString()} requests` : 'Logs'}
          </span>
          {loading && <span style={{ fontSize: '12px', color: '#9ca3af' }}>Loading...</span>}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                {['Time', 'Cat', 'Method', 'What Happened', 'Status', 'Duration', 'User', 'IP'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                    {loading ? 'Loading logs...' : 'No logs found'}
                  </td>
                </tr>
              ) : logs.map(log => {
                const mc  = METHOD_COLORS[log.method] || { bg: '#f3f4f6', color: '#374151' };
                const sc  = statusColor(log.status);
                const cat = deriveCategory(log.url, log.category);
                const cc  = CATEGORY_COLORS[cat] || { bg: '#f3f4f6', color: '#6b7280' };

                // Highlight payment rows with a subtle left border
                const isPayment = cat === 'payments';
                const isError   = log.status >= 400;

                return (
                  <tr key={log._id}
                    onClick={() => setSelectedLog(log)}
                    style={{
                      borderBottom: '1px solid #f9fafb',
                      cursor: 'pointer',
                      transition: 'background 0.1s',
                      borderLeft: isPayment ? '3px solid #f59e0b' : isError ? '3px solid #ef4444' : '3px solid transparent',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>

                    {/* Time */}
                    <td style={{ padding: '10px 16px', color: '#9ca3af', whiteSpace: 'nowrap', fontFamily: "'DM Mono', monospace", fontSize: '11px' }}>
                      {new Date(log.createdAt).toLocaleString('en-ZA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </td>

                    {/* Category */}
                    <td style={{ padding: '10px 16px' }}>
                      <Pill bg={cc.bg} color={cc.color}>{cat}</Pill>
                    </td>

                    {/* Method */}
                    <td style={{ padding: '10px 16px' }}>
                      <Pill bg={mc.bg} color={mc.color}>{log.method}</Pill>
                    </td>

                    {/* Summary — the human-readable "what happened" column */}
                    <td style={{ padding: '10px 16px', maxWidth: '380px' }}>
                      {log.summary ? (
                        <p style={{ margin: 0, fontSize: '12px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={log.summary}>
                          {log.summary}
                        </p>
                      ) : (
                        <p style={{ margin: 0, fontFamily: "'DM Mono', monospace", fontSize: '12px', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.url}
                        </p>
                      )}
                    </td>

                    {/* Status */}
                    <td style={{ padding: '10px 16px' }}>
                      <Pill bg={sc.bg} color={sc.color}>{log.status}</Pill>
                    </td>

                    {/* Duration */}
                    <td style={{ padding: '10px 16px', fontFamily: "'DM Mono', monospace", fontSize: '12px', color: durationColor(log.responseTimeMs), whiteSpace: 'nowrap' }}>
                      {log.responseTimeMs}ms
                    </td>

                    {/* User */}
                    <td style={{ padding: '10px 16px', maxWidth: '180px' }}>
                      {log.userEmail
                        ? <div>
                            <p style={{ margin: 0, fontSize: '12px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.userEmail}</p>
                            <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#9ca3af', textTransform: 'capitalize' }}>{log.userRole}</p>
                          </div>
                        : <span style={{ color: '#d1d5db', fontSize: '12px' }}>—</span>
                      }
                    </td>

                    {/* IP */}
                    <td style={{ padding: '10px 16px', fontFamily: "'DM Mono', monospace", fontSize: '11px', color: '#9ca3af' }}>
                      {log.ip}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ─────────────────────────────────────────────────── */}
        {meta && meta.pages > 1 && (
          <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: '#9ca3af' }}>
              Page {meta.page} of {meta.pages}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setPage(p => p - 1)} disabled={!meta.hasPrevPage}
                style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '13px', color: meta.hasPrevPage ? '#374151' : '#d1d5db', cursor: meta.hasPrevPage ? 'pointer' : 'not-allowed' }}>
                Previous
              </button>
              <button onClick={() => setPage(p => p + 1)} disabled={!meta.hasNextPage}
                style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '13px', color: meta.hasNextPage ? '#374151' : '#d1d5db', cursor: meta.hasNextPage ? 'pointer' : 'not-allowed' }}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Detail Modal ───────────────────────────────────────────────── */}
      {selectedLog && (
        <div onClick={() => setSelectedLog(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '600px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>Request Detail</h2>
              <button onClick={() => setSelectedLog(null)}
                style={{ background: 'none', border: 'none', fontSize: '20px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            {/* Summary banner — most important field */}
            {selectedLog.summary && (
              <div style={{ background: '#f8f7f5', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#374151', lineHeight: 1.6 }}>
                {selectedLog.summary}
              </div>
            )}

            {[
              ['Time',      new Date(selectedLog.createdAt).toLocaleString('en-ZA')],
              ['Category',  selectedLog.category || '—'],
              ['Method',    selectedLog.method],
              ['URL',       selectedLog.url],
              ['Status',    String(selectedLog.status)],
              ['Duration',  `${selectedLog.responseTimeMs}ms`],
              ['IP',        selectedLog.ip],
              ['User',      selectedLog.userEmail || '—'],
              ['Role',      selectedLog.userRole  || '—'],
              ['User ID',   selectedLog.userId    || '—'],
              ['User Agent', selectedLog.userAgent || '—'],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', padding: '10px 0', borderBottom: '1px solid #f3f4f6', gap: '16px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#9ca3af', width: '90px', flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em', paddingTop: '1px' }}>{label}</span>
                <span style={{ fontSize: '13px', color: '#111827', fontFamily: ['URL', 'IP', 'User Agent', 'User ID'].includes(label) ? "'DM Mono', monospace" : undefined, wordBreak: 'break-all' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Logs;
